/**
 * @file useAddMember.js
 * @module src/js/hooks/useAddMember
 * @description
 * **A hook to add a new member by sending their details to the backend API.**
 * - Utilizes React contexts to manage user state and modal messages.
 * - Returns a function that:
 *   - Sends a POST request to the backend.
 *   - Handles file upload if a photo is provided.
 *   - Updates the frontend state based on the response.
 *
 * @version
 * <p>
 *  2025-11-29: First version with documentation. (Zong-Han Wu, zhwu@eda.ee.ntu.edu.tw)<br/>
 *  2025-12-02: Migrate to useFetchFromBackend. (Zong-Han Wu, zhwu@eda.ee.ntu.edu.tw)<br/>
 * </p>
 */
import { useCallback } from "react";
import { useLocation } from "react-router-dom";
import { useModal } from "../contexts/ModalProvider.js";
import useFetchFromBackend from "./useFetchFromBackend";

export default function useAddMember({
    setMemberID,
    setInstitute,
    setLevel,
    setPassword,
    setPasswordForConfirmation,
    setName,
    setImgFile,
    memberIDRef,
    setUpdating,
}) {
    const { pathname } = useLocation();
    const { showModalMessage } = useModal();
    const fetchFromBackend = useFetchFromBackend();

    if (typeof setMemberID !== 'function' ||
        typeof setInstitute !== 'function' ||
        typeof setLevel !== 'function' ||
        typeof setPassword !== 'function' ||
        typeof setPasswordForConfirmation !== 'function' ||
        typeof setName !== 'function' ||
        typeof setImgFile !== 'function'){
        throw new Error("All setter functions must be provided to useAddMember hook.");
    }

    const addMember = useCallback(async ({
        body,
        imgFile
    }) => {
        if (!body || typeof body !== 'object' ||
            !body.id || !body.institute || !body.level ||
            !body.pass || !body.name || !('photo' in body)) {
            throw new Error("All member details must be provided to add a new member.");
        }

        try {
            setUpdating(true);
            showModalMessage({ type: 'info', content: 'Adding...' });

            let formData = new FormData();
            if (body.photo !== 'none') {
                Object.entries(body).forEach(([key, value]) => {
                    formData.append(key, value);
                });
                formData.append("file[]", imgFile);
            }

            const data = await fetchFromBackend({
                request: `/api/profile/${body.id}`,
                method: "POST",
                body: body.photo !== 'none' ? formData : body,
                uploadFile: body.photo !== 'none',
                requireAuth: true,
                from: pathname,
            });
            
            if (data?.success) {
                showModalMessage({ type: "success", content: "Added successfully! Check the Members page or add another one." });
                setMemberID('');
                setInstitute('choose');
                setLevel(-1);
                setPassword('');
                setPasswordForConfirmation('');
                setName('');
                setImgFile([null]);
                if (memberIDRef.current) {
                    memberIDRef.current.focus();
                }
                
            } else if (data?.error === "Duplicate member_ID") {
                showModalMessage({ type: "error", content: 'This username already exists. Please use a different username.' });

            } else if (data?.error === "Only an administrator can add new members.") {
                showModalMessage({ type: "error", content: 'Only an administrator can add new members. Please ask the administrator to create the account.' });

            } else if (data?.error) {
                console.error("Failed to add:", data.error);
                showModalMessage({ type: "error", content: 'Failed to add. Please try again.' });
            }
            
        } catch (error) {
            console.error("An error occurred:", error);
            showModalMessage({ type: "error", content: "An error occurred. Please try again." });

        } finally {
            setUpdating(false);
        }
    }, [
        fetchFromBackend,
        memberIDRef,
        pathname,
        setImgFile,
        setInstitute,
        setLevel,
        setMemberID,
        setName,
        setPassword,
        setPasswordForConfirmation,
        setUpdating,
        showModalMessage
    ]);

    return addMember;
}